import { action, computed, makeObservable } from 'mobx';

import { moment } from '@feathr/hooks';
import type {
  Attributes,
  DeepObservable,
  IBaseAttributes,
  IMetadata,
  IWretchResponseValid,
  TConstraints,
  TRachisEmpty,
} from '@feathr/rachis';
import { Collection, isWretchError, Model, wretch } from '@feathr/rachis';

import { CAMPAIGN_DEFAULT_MAX_BUDGET, CAMPAIGN_MAX_BUDGET, CAMPAIGN_MIN_BUDGET } from './constants';
import type { TUnitOfTimeConversions } from './conversions';
import type { IFacebookError, IToSAccepted } from './facebook_ad_accounts';
import { feathrFacebookErrorMap } from './facebook_ad_accounts';
import type { TFlagsRecord } from './flags';
import { EIntegrationTypes } from './integrations';
import type { IAddOn, ILicense, IPackage, IProduct, IService } from './licenses';
import { EPackages, EServiceItemIds } from './licenses';
import type { EOnboardingMilestone, IOnboardingTask } from './onboarding_tasks';
import type { TAttributionModel } from './stats';
import { EUserRoleIDs } from './user_roles';
import type { User } from './users';

export const INTERNAL_TEST_ACCOUNT_IDS = [
  // Feathr Test Account
  '000000000000000000000000',
  // Feathr Test Account (Pro)
  '101010101010101010101010',
  // Feathr Test Account (Lite)
  '101101101101101101101101',
  // Dead Cafe - Dev/Platform Test Account
  'dead0000000000000000cafe',
  // Beef - QE automation account
  'beeeeeeeeeeeeeeeeeeeeeef',
  // Bad - local
  'badbadbadbadbadbadbadbad',
  // David Test Account (Lite)
  '102102102102102102102102',
  // Jeff Test Account (Lite)
  '103103103103103103103103',
  // Jesse Test Account (Lite)
  '104104104104104104104104',
  // Lucas Test Account (Lite)
  '105105105105105105105105',
  // Omar Test Account (Lite)
  '106106106106106106106106',
  // Otis Test Account (Lite)
  '107107107107107107107107',
  // Paul Test Account (Lite)
  '108108108108108108108108',
  // Thomas Test Account (Lite)
  '109109109109109109109109',
  // Feathr Test Account (Lite 2)
  '202202202202202202202202',
  // Feathr Test Account (Lite 3)
  '303303303303303303303303',
  // Feathr Test Account (Lite 4)
  '404404404404404404404404',
  // Feathr Test Account (Lite 5)
  '505505505505505505505505',
];

export enum FacebookAccessTokenHealth {
  ok = 'ok',
  expired = 'expired',
}

export enum FacebookIntegrationState {
  not_connected = 'Not Connected',
  no_page_selected = 'No Page Selected',
  page_selected = 'Page Selected',
  access_token_pending = 'Access Token Pending',
  payment_method_pending = 'Payment Method Pending',
  ad_account_pending = 'Ad Account Pending',
  failed = 'Failed',
}

export interface IFacebook extends Record<string, string | undefined | IToSAccepted> {
  id?: string;
  name: string;
  user_id: string;
  username: string;
  access_token: string;
  business_manager_access_token: string;
  access_token_health: FacebookAccessTokenHealth;
  state: FacebookIntegrationState;
  date_token_expiration?: string;
  pixel_id?: string;
  tos_accepted: IToSAccepted;
}

export interface IAccountUserRole {
  /** The ID of the user */
  user: string;
  /** The ID of the user's role */
  role: string;
}

interface IMappingProps {
  CategoryTaxonomyId: number;
  CategoryId: number;
  ExternalId: string;
}

export interface ITradeDeskCategory extends Record<string, unknown> {
  CategoryId: number;
  Children: ITradeDeskCategory[];
  ExternalId: number;
  IsSensitive: boolean;
  MappingInformation: IMappingProps[];
  Name: string;
  ParentCategoryId: number | null;
  Path: string;
}

export interface ITTDSubAdvertiser {
  subadv_id: string;
  id?: string;
  logo?: string;
  name?: string;
  url?: string;
}

interface ICrossDeviceVendor {
  vendor_cpm_rate?: number;
  vendor_id: number;
  vendor_name?: string;
}

export interface ITTDAdvertiser {
  advertiser_category_id?: number;
  advertiser_category?: string;
  // TODO: make this required when there's time to test all the implications
  allow_cross_device?: boolean;
  /** Cross device vendors get downsynced from TTD by a job */
  available_cross_device_vendors?: ICrossDeviceVendor[];
  subadvertisers?: ITTDSubAdvertiser[];
  /** Optional ID because TTD parent is possibly undefined */
  id?: string;
}

export const advertiserConstraints = {
  name: {
    presence: {
      allowEmpty: false,
    },
  },
  url: {
    url: {
      message: '^Website URL must be a valid URL.',
    },
  },
  logo: {
    url: {
      message: '^Please upload a logo file or provide a logo URL.',
    },
  },
};

export type TEmailHealth = 'healthy' | 'review' | 'suspended';

interface IAccountSettings {
  require_mfa?: boolean;
  // TODO: make this required when there's time to test all the implications
  /** Currently synced with cross device targeting in ttd.allow_cross_device */
  allow_early_access?: boolean;
  beefree_dev_mode?: boolean;
  campaigns_max_budget?: number;
  confirmed_bbrenxt_marketplace_sync?: boolean;
}

export interface IEmailPreferences {
  /** An alias for account.name that's displayed on the Subscription management page */
  alias?: string;
  /** The URL to the favicon that's displayed on the Subscription management page. If not provided, the Feathr favicon is used. */
  favicon_url?: string;
  /** The URL to the logo that's displayed on the Subscription management page. If not provided, the Feathr logo is used. */
  logo_url?: string;
  /** The last time email preferences for changed. */
  date_last_modified?: string;
}

export interface IAccount extends IBaseAttributes {
  attribution_model: TAttributionModel;
  csm: string;
  date_created: string;
  date_last_modified: string;
  description?: string;
  domain_allow_list?: string[];
  email_health: TEmailHealth;
  email_preferences: IEmailPreferences;
  facebook: IFacebook;
  flags: TFlagsRecord;
  integrations: EIntegrationTypes[];
  ip_filters: string[];
  is_agency: boolean;
  /** True when the account is internal to Feathr */
  is_internal: boolean;
  /** For reading license is ILicense, but for writing license is Partial<ILicense> */
  license: ILicense;
  logo: string;
  media_validation: boolean;
  multi_convert_cooldown_unit: TUnitOfTimeConversions;
  multi_convert_cooldown_value: number;
  name: string;
  settings: IAccountSettings;
  /**
   * Strategists are now called Pro's
   *
   * The strategic advisor assigned to this account. Pro's provide
   * advice on campaign ideas, optimizations, campaign health reviews,
   * and advanced training topics.
   */
  strategist?: string;
  ttd?: ITTDAdvertiser;
  url: string;
  /** Formerly ACL */
  user_roles: IAccountUserRole[];
}

export class Account extends Model<IAccount> {
  public readonly className = 'Account';
  public override collection: Accounts<this> | null = null;

  public static readonly defaults: Partial<IAccount> = {
    email_health: 'healthy',
    integrations: [EIntegrationTypes.Tradedesk],
    settings: {
      allow_early_access: true,
    },
    ttd: {
      allow_cross_device: true,
      available_cross_device_vendors: [],
    },
    license: {
      active_services: [],
      active_add_ons: [],
      add_ons: [],
      autorenew: false,
      discounts: [],
      packages: [],
      services: [],
    },
  };

  public get constraints(): TConstraints<IAccount> {
    return {
      'settings.campaigns_max_budget': {
        numericality: {
          greaterThanOrEqualTo: CAMPAIGN_MIN_BUDGET,
          lessThanOrEqualTo: CAMPAIGN_MAX_BUDGET,
        },
      },
      name: {
        presence: {
          allowEmpty: false,
        },
      },
      logo: {
        presence: {
          allowEmpty: false,
          message: '^The Trade Desk requires a logo.',
        },
        url: true,
      },
      url: {
        presence: {
          allowEmpty: false,
        },
        url: true,
      },
      domain_allow_list: {
        list: {
          presence: {
            allowEmpty: false,
            message: '^Domain cannot be blank.',
          },
          urlWithoutProtocol: true,
        },
        duplicates: {
          maximum: 0,
          message: '^Domain must be unique.',
        },
      },
      multi_convert_cooldown_value: {
        presence: {
          allowEmpty: false,
          message: '^Cooldown value cannot be blank.',
        },
        numericality: {
          greaterThan: 0,
          message: '^Cooldown value must be a number greater than 0.',
        },
      },
    };
  }

  constructor(attributes: Partial<IAccount> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<IAccount> {
    return Account.defaults;
  }

  @computed
  public get activePackage(): IPackage | undefined {
    return this.get('license').active_package ?? undefined;
  }

  @computed
  public get campaignsMaxBudget(): number {
    return this.get('settings').campaigns_max_budget ?? CAMPAIGN_DEFAULT_MAX_BUDGET;
  }

  @computed
  public get hasValidLicensePackageName(): boolean {
    if (!this.activePackage) {
      return false;
    }

    const { name: activePackageName } = this.activePackage;

    const validLicensePackagesNames = Object.values(EPackages);
    return activePackageName ? validLicensePackagesNames.includes(activePackageName) : false;
  }

  @computed
  public get hasActivePackage(): boolean {
    return !!(this.activePackage && this.hasValidLicensePackageName);
  }

  @computed
  public get activeServices(): IService[] {
    return this.get('license').active_services ?? [];
  }

  @computed
  public get activeAddOns(): IAddOn[] {
    return this.get('license').active_add_ons ?? [];
  }

  @computed
  public get addOns(): IAddOn[] {
    return this.get('license').add_ons ?? [];
  }

  @computed
  public get adminUserIds(): string[] {
    return this.get('user_roles', [])
      .filter((userRole) => userRole.role === EUserRoleIDs.Admin)
      .map((role) => role.user);
  }

  @computed
  public get isPlatformLite(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return this.activePackage.name === EPackages.FeathrPlatformLite;
  }

  // This license package is considered the "base" package and has access to the basic features and essential pro features.
  @computed
  public get isPlatformBase(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return this.activePackage.name === EPackages.FeathrPlatformBase;
  }

  /*
   * These license packages are considered "platform starter" and have access to the basic features.
   * The "platform starter" packages have identical feature access and limits as "platform essential".
   */
  @computed
  public get isPlatformStarter(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [EPackages.FeathrPlatformStarter].includes(this.activePackage.name);
  }

  /*
   * These license packages are considered "platform essential" and have access to the basic features.
   * The "platform starter" packages have identical feature access and limits as "platform essential".
   */
  @computed
  public get isPlatformEssential(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return (
      [
        // Platform Essential <<Basic>>
        EPackages.FeathrPlatformEssential,
        // Platform Essential <<Pro>>
        EPackages.FeathrPlatformEssentialPro,
      ].includes(this.activePackage.name) || this.isPlatformStarter
    );
  }

  // These license packages are considered "platform advanced" and have access to the advanced features
  @computed
  public get isPlatformAdvanced(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [
      // Platform Advanced <<Basic>>
      EPackages.FeathrPlatformAdvanced,
      // Platform Advanced <<Pro>>
      EPackages.FeathrPlatformAdvancedPro,
    ].includes(this.activePackage.name);
  }

  // These license packages are considered "platform enterprise" and have access to the enterprise features
  @computed
  public get isPlatformEnterprise(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [
      // Platform <<Enterprise>> Basic
      EPackages.FeathrPlatformEnterprise,
      // Platform <<Enterprise>> Pro
      EPackages.FeathrPlatformEnterprisePro,
    ].includes(this.activePackage.name);
  }

  // The <<Basic>> license packages have access to the basic features
  @computed
  public get isBasic(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [
      // Platform <<Essential>> Basic
      EPackages.FeathrPlatformEssential,
      // Platform <<Advanced>> Basic
      EPackages.FeathrPlatformAdvanced,
      // Platform <<Enterprise>> Basic
      EPackages.FeathrPlatformEnterprise,
    ].includes(this.activePackage.name);
  }

  // The <<Pro>> license packages have access to the pro features
  @computed
  public get isPro(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [
      // Platform <<Base>> (has access to essential pro features)
      EPackages.FeathrPlatformBase,
      // Platform <<Essential>> Pro
      EPackages.FeathrPlatformEssentialPro,
      // Platform <<Advanced>> Pro
      EPackages.FeathrPlatformAdvancedPro,
      // Platform <<Enterprise>> Pro
      EPackages.FeathrPlatformEnterprisePro,
    ].includes(this.activePackage.name);
  }

  // These are the license packages that have access to the <<Feathr Platform>>
  @computed
  public get isAnyFeathrPlatform(): boolean {
    return (
      // Platform Base
      this.isPlatformBase ||
      // Platform Starter Basic and Pro
      this.isPlatformStarter ||
      // Platform Essential Basic and Pro
      this.isPlatformEssential ||
      // Platform Advanced Basic and Pro
      this.isPlatformAdvanced ||
      // Platform Enterprise Basic and Pro
      this.isPlatformEnterprise
    );
  }

  /** @deprecated */
  @computed
  public get isEnterprise(): boolean {
    return [
      EPackages.EnterpriseFalcon,
      EPackages.EnterpriseFlamingo,
      EPackages.EnterpriseMarketingSeat,
      EPackages.EnterpriseMonetizationSeat,
      EPackages.EnterpriseUnlimitedMarketingSeats,
      EPackages.EnterpriseUnlimitedMonetizationSeats,
      EPackages.EnterpriseFalconSeat,
      EPackages.EnterpriseFalconUnlimitedSeats,
      // Platform Enterprise is not Enterprise
    ].includes(this.activePackage?.name ?? EPackages.Finch);
  }

  /** @deprecated */
  @computed
  public get isEnterpriseUnlimited(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return [
      EPackages.EnterpriseUnlimitedMarketingSeats,
      EPackages.EnterpriseUnlimitedMonetizationSeats,
      EPackages.EnterpriseFalconUnlimitedSeats,
      // Platform Enterprise is not Enterprise Unlimited
    ].includes(this.activePackage.name);
  }

  /** @deprecated */
  @computed
  public get isPhoenix(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return (
      this.isAnyFeathrPlatform ||
      [EPackages.Phoenix, EPackages.PhoenixLicenseExtended, EPackages.PhoenixLicense].includes(
        this.activePackage.name,
      )
    );
  }

  /** @deprecated */
  @computed
  public get isFalcon(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return (
      this.isPhoenix ||
      this.isEnterprise ||
      this.isAnyFeathrPlatform ||
      [EPackages.Falcon, EPackages.FalconLicense, EPackages.FalconLicenseExtended].includes(
        this.activePackage.name,
      )
    );
  }

  /** @deprecated */
  @computed
  public get isFinch(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return (
      this.isAnyFeathrPlatform ||
      [
        EPackages.Finch,
        EPackages.FinchLegacy,
        EPackages.FinchLicense,
        EPackages.FinchLicenseExtended,
      ].includes(this.activePackage.name)
    );
  }

  /** @deprecated */
  @computed
  public get isFinchLegacy(): boolean {
    if (!this.activePackage) {
      return false;
    }
    return (
      this.isFalcon ||
      [EPackages.FinchLegacy, EPackages.FinchLegacyLicense].includes(this.activePackage.name)
    );
  }

  @computed
  public get isInternal(): boolean {
    return this.get('is_internal') ?? false;
  }

  @computed
  public get isTestAccount(): boolean {
    return INTERNAL_TEST_ACCOUNT_IDS.includes(this.id);
  }

  @computed
  public get primaryBillableId(): string | undefined {
    return this.get('license').billable ?? undefined;
  }

  @computed
  public get hasServices(): boolean {
    const services = this.activeServices;
    return !!services && services.length > 0;
  }

  @computed
  public get hasActiveSuperpixel(): boolean {
    return this.get('onboarding_steps').setup_super_pixel ?? false;
  }

  @computed
  public get hasPixelImplementation(): boolean {
    const services = this.activeServices.filter((service) =>
      [
        EServiceItemIds.CoPilotLegacy,

        EServiceItemIds.CampaignManagement,
        EServiceItemIds.CreativeMigrationPack,
        EServiceItemIds.DataIntegration,
        EServiceItemIds.PremiumOnboarding,
        EServiceItemIds.Training,
      ].includes(service.item_id),
    );
    return (!!services && services.length > 0) || this.isPro;
  }

  @computed
  public get hasInvites(): boolean {
    // Finch and Finch Legacy do not have invites.
    return this.isAnyFeathrPlatform || this.isFalcon || this.isEnterprise || this.isPhoenix;
  }

  @computed
  public get hasMonetization(): boolean {
    if (!this.activePackage) {
      return false;
    }
    // Finch, Finch Legacy, and Enterprise Flamingo do not have monetization.
    if (
      [EPackages.Finch, EPackages.FinchLegacy, EPackages.EnterpriseFlamingo].includes(
        this.activePackage.name,
      )
    ) {
      return false;
    }

    const { isAnyFeathrPlatform, isFalcon, isEnterprise, isPhoenix } = this;
    return isAnyFeathrPlatform || isFalcon || isEnterprise || isPhoenix;
  }

  @computed
  public get hasEarlyAccess(): boolean {
    // TODO: this should never return undefined after we update the type to be required
    return this.get('settings').allow_early_access ?? true;
  }

  // TODO: This should be moved off Account.ts
  @computed
  public get getFacebookAccessToken(): string | undefined {
    const facebook = this.get('facebook') as IFacebook;
    if (!facebook) {
      return undefined;
    }
    return facebook.business_manager_access_token;
  }

  public validateFacebookIntegration(): IFacebookError[] {
    const errors: IFacebookError[] = [];
    const integration = this.get('facebook');

    if (!integration) {
      errors.push(feathrFacebookErrorMap.account_integration_not_found);
      return errors;
    }
    const dateTokenExpiration = moment.utc(integration.date_token_expiration, moment.ISO_8601);
    const healthyToken =
      integration.access_token_health === FacebookAccessTokenHealth.ok &&
      dateTokenExpiration.isAfter(moment.utc());
    if (!healthyToken) {
      errors.push(feathrFacebookErrorMap.token_expired);
      return errors;
    }
    if (integration.state !== FacebookIntegrationState.page_selected) {
      errors.push(feathrFacebookErrorMap.account_integration_not_ready);
      return errors;
    }
    return errors;
  }

  public getAdvertiser(advId: string): ITTDSubAdvertiser | undefined {
    return (this.get('ttd', { subadvertisers: [] }).subadvertisers || []).find(
      (subadv) => subadv.subadv_id === advId,
    );
  }

  public get emailPreferencesPreviewUrl(): string {
    return `${POLO_URL}email_preferences?a_id=${this.id}&is_preview=1`;
  }

  /*
   * Account can add project if:
   * 1. User is sudoer (Account Manager or superuser)
   * 2. Account is Essential or Advanced (any package)
   * 3. Account is not Enterprise
   * 4. Account is Enterprise Unlimited
   */
  public canAddProject(user: User): boolean {
    if (!user) {
      return false;
    }
    return (
      user.isSudoer || this.isAnyFeathrPlatform || this.isEnterpriseUnlimited || !this.isEnterprise
    );
  }

  /*
   * Returns a basic list of integrations on the account. In the Anhinga,
   * integration controllers add and remove the integration name from this list.
   * By default, all accounts have the Tradedesk integration.
   */
  public get integrations(): EIntegrationTypes[] {
    return this.get('integrations', []);
  }

  /*
   * Returns a list of integrations that conflict with the current integration.
   * For example, if the account has iMIS then it can't have Blackbaud Raiser's Edge NXT.
   * If the account has Blackbaud Raiser's Edge NXT then it can't have iMIS.
   * This is because both integrations would be trying to sync the same data.
   */
  @computed
  public get conflictingIntegrations(): EIntegrationTypes[] {
    const conflictingIntegrations: EIntegrationTypes[] = [];

    // If the account has iMIS then it can't have Blackbaud Raiser's Edge NXT.
    if (this.integrations.includes(EIntegrationTypes.Imis)) {
      conflictingIntegrations.push(EIntegrationTypes.BlackbaudRaisersEdge);
    }

    // If the account has Blackbaud Raiser's Edge NXT then it can't have iMIS.
    if (this.integrations.includes(EIntegrationTypes.BlackbaudRaisersEdge)) {
      conflictingIntegrations.push(EIntegrationTypes.Imis);
    }
    return conflictingIntegrations;
  }

  @computed
  public get hasConflictingIntegrations(): boolean {
    return this.conflictingIntegrations.length > 0;
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed Account';
  }

  public async updateLicense(patch: IProduct): Promise<IWretchResponseValid<IAccount, IMetadata>> {
    this.assertCollection(this.collection);
    return this.collection.updateLicense(this.id, patch);
  }

  public async getIndustryCategories(): Promise<
    IWretchResponseValid<ITradeDeskCategory[], IMetadata>
  > {
    this.assertCollection(this.collection);
    return this.collection.getIndustryCategories();
  }

  public async updateIndustryCategory(
    industryCategoryName: string,
    industrySubCategoryName: string | undefined,
  ): Promise<IWretchResponseValid<IAccount, IMetadata>> {
    this.assertCollection(this.collection);
    return this.collection.updateIndustryCategory(industryCategoryName, industrySubCategoryName);
  }

  public getSetting<K extends keyof IAccountSettings>(
    attribute: K,
  ): DeepObservable<IAccountSettings[K]>;

  public getSetting<K extends keyof IAccountSettings>(
    attribute: K,
    defaultValue: Exclude<IAccountSettings[K], undefined>,
  ): DeepObservable<Exclude<IAccountSettings[K], undefined>>;

  public getSetting<K extends keyof IAccountSettings>(key: K, defaultValue?: unknown): unknown {
    const settings: IAccountSettings = this.get('settings', {});
    return settings[key] ?? defaultValue;
  }

  @action
  public setSetting<K extends keyof IAccountSettings>(key: K, value: IAccountSettings[K]): void {
    const settings: IAccountSettings = this.get('settings', {});
    this.set({ settings: { ...settings, [key]: value } });
  }

  public async resetOnboarding(
    params: {
      milestones?: EOnboardingMilestone[];
      tasks?: Array<IOnboardingTask['_cls']>;
    } = {},
  ): Promise<IWretchResponseValid<TRachisEmpty, IMetadata>> {
    this.assertCollection(this.collection);
    const url = this.collection.url('purge-onboarding', this.id);
    const headers = this.collection.getHeaders();

    // Onboarding is only available for Feathr Lite.
    const req = { package_name: EPackages.FeathrPlatformLite };

    if (params.milestones && params.milestones.length > 0) {
      Object.assign(req, { milestones: params.milestones });
    }
    if (params.tasks && params.tasks.length > 0) {
      Object.assign(req, { tasks: params.tasks });
    }

    const response = await wretch<TRachisEmpty>(url, {
      headers,
      method: 'POST',
      body: JSON.stringify(req),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response;
  }

  public async changeLicensePackage(
    packageName: EPackages,
    startDate?: string,
  ): Promise<IWretchResponseValid<TRachisEmpty, IMetadata>> {
    this.assertCollection(this.collection);
    const url = this.collection.url('update-license', this.id);
    const headers = this.collection.getHeaders();
    const params = { package_name: packageName };

    if (startDate) {
      Object.assign(params, { start_date: startDate });
    }

    const response = await wretch<TRachisEmpty>(url, {
      headers,
      body: JSON.stringify(params),
      method: 'POST',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response;
  }
}

export class Accounts<Model extends Account = Account> extends Collection<Model> {
  public getModel(attributes: Partial<Attributes<Model>>): Model {
    return new Account(attributes) as Model;
  }

  public getClassName(): string {
    return 'accounts';
  }

  public override url(variant: string, value?: string, method?: string): string;

  public override url(variant?: string, value?: string, method?: string): string {
    if (variant === 'license') {
      return `${this.getHostname()}accounts/${value}/${variant}`;
    } else if (variant === 'ttd' && method === 'GET') {
      return `${this.getHostname()}${variant}/industry_categories`;
    } else if (variant === 'ttd' && method === 'PATCH') {
      return `${this.getHostname()}accounts/${variant}`;
    } else if (variant === 'purge-onboarding') {
      return `${this.getHostname()}accounts/${value}/purge-onboarding`;
    } else if (variant === 'update-license') {
      return `${this.getHostname()}accounts/${value}/update-license`;
    }
    return super.url();
  }

  @action
  public async updateLicense(
    accountId: string,
    patch: IProduct,
  ): Promise<IWretchResponseValid<IAccount, IMetadata>> {
    const url = this.url('license', accountId);
    const headers = this.getHeaders();
    const response = await wretch<IAccount>(url, {
      headers,
      body: JSON.stringify(patch),
      method: 'PATCH',
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response;
  }

  public async getIndustryCategories(): Promise<
    IWretchResponseValid<ITradeDeskCategory[], IMetadata>
  > {
    const url = this.url('ttd', '', 'GET');
    const headers = this.getHeaders();
    const response = await wretch<ITradeDeskCategory[]>(url, { headers, method: 'GET' });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response;
  }

  @action
  public async updateIndustryCategory(
    industryCategoryName: string,
    industrySubCategoryName: string | undefined,
  ): Promise<IWretchResponseValid<IAccount, IMetadata>> {
    const url = this.url('ttd', '', 'PATCH');
    const headers = this.getHeaders();
    const response = await wretch<IAccount>(url, {
      headers,
      body: JSON.stringify({
        category_name: industryCategoryName,
        subcategory_name: industrySubCategoryName,
      }),
      method: 'PATCH',
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response;
  }
}
